import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { deleteEntry, getEntry } from '../../db/entries';
import { getCategoryMap } from '../../db/categories';
import { emptyProjects, getProjectsDoc } from '../../db/projects';
import { useAsync } from '../hooks/useAsync';
import { useDataVersion } from '../../db/events';
import { formatDateJa, formatTime, parseIso } from '../../lib/datetime';
import { EntryBadge } from '../components/EntryBadge';
import type { Category, ProjectsDoc } from '../../domain/types';
import type { EntryRecord } from '../../db/schema';
import { setListPath } from '../layout/listPath';
import { Lightbox, MediaImage } from '../components/MediaImage';
import { formatLatLng, mapUrl } from '../../lib/location';

export function EntryDetail() {
  const version = useDataVersion();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState<number | null>(null);
  const entry = useAsync<EntryRecord | undefined>(() => getEntry(id), [id, version], undefined);
  const categories = useAsync<Map<string, Category>>(getCategoryMap, [version], new Map());
  const projects = useAsync<ProjectsDoc>(getProjectsDoc, [version], emptyProjects());

  // 分割ペインの左側を、この記事が属する日に追従させる
  useEffect(() => {
    if (entry) setListPath(`/day/${entry.dateKey}`);
  }, [entry]);

  if (!entry) return <div className="view"><div className="view-body empty"><p>記録が見つかりません</p></div></div>;

  const d = parseIso(entry.date);

  return (
    <div className="view">
      <header className="view-head">
        <div className="view-title">
          <h1>{entry.title || '（無題）'}</h1>
          <span className="summary">
            {formatDateJa(d)} {formatTime(entry.date)}
          </span>
        </div>
        <Link className="today-btn" to={`/entry/${entry.id}/edit`}>
          編集
        </Link>
        <button
          className="today-btn danger"
          onClick={async () => {
            if (!confirm('この記録を削除します。元に戻せません。')) return;
            const dateKey = entry.dateKey;
            await deleteEntry(entry.id);
            navigate(`/day/${dateKey}`, { replace: true });
          }}
        >
          削除
        </button>
      </header>
      <div className="view-body detail">
        <div className="detail-meta">
          <EntryBadge categoryId={entry.categoryId} projectId={entry.projectId} categories={categories} projects={projects} />
          {entry._dirty && <span className="muted">未同期</span>}
        </div>
        {entry.body && <p className="detail-body">{entry.body}</p>}
        {entry.media.length > 0 && (
          <div className="detail-media">
            {entry.media.map((m, i) => (
              <MediaImage
                key={m.id}
                media={m}
                month={entry.monthKey}
                entryId={entry.id}
                className="detail-img"
                onClick={() => setLightbox(i)}
              />
            ))}
          </div>
        )}
        {lightbox !== null && (
          <Lightbox
            media={entry.media}
            month={entry.monthKey}
            entryId={entry.id}
            index={lightbox}
            onClose={() => setLightbox(null)}
            onMove={setLightbox}
          />
        )}
        {entry.location && (
          <a className="detail-loc" href={mapUrl(entry.location)} target="_blank" rel="noreferrer">
            📍 {formatLatLng(entry.location)}
            <span className="badge badge-plain">{entry.location.source === 'exif' ? 'EXIF' : '端末'}</span>
            {entry.location.accuracy != null && (
              <span className="muted"> ±{Math.round(entry.location.accuracy)}m</span>
            )}
          </a>
        )}
        <dl className="kv small">
          <dt>作成</dt>
          <dd>{new Date(entry.createdAt).toLocaleString('ja-JP')}</dd>
          <dt>更新</dt>
          <dd>{new Date(entry.updatedAt).toLocaleString('ja-JP')}（rev {entry.rev}）</dd>
        </dl>
      </div>
    </div>
  );
}
